import { useState } from 'react'
import { Archive, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BOARD_STAGES, formatDay, type TaskStage, type TaskView } from '@/lib/tasks-board'
import type { WaitingInput } from '@/services/task-board-service'
import { cn } from '@/utils/cn'

interface TaskStageControlProps {
  task: TaskView
  busy: boolean
  onMove: (stage: TaskStage, waiting?: WaitingInput) => void
  onReopen: () => void
}

/** Etapa da tarefa (A fazer → Concluída), Aguardando com de quem/o quê/retorno, reabrir e arquivar. */
export function TaskStageControl({ task, busy, onMove, onReopen }: TaskStageControlProps) {
  const [askingWaiting, setAskingWaiting] = useState(false)
  const [waiting, setWaiting] = useState<WaitingInput>({
    waiting_on: task.waiting_on ?? '',
    waiting_for: task.waiting_for ?? '',
    waiting_until: task.waiting_until?.slice(0, 10) ?? '',
  })
  const waitingValid = waiting.waiting_on.trim() && waiting.waiting_for.trim() && waiting.waiting_until

  const choose = (stage: TaskStage) => {
    if (stage === 'AGUARDANDO') setAskingWaiting(true)
    else onMove(stage)
  }

  if (task.etapa === 'ARQUIVADA') {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm">
        <span>Arquivada{task.status === 'CANCELLED' ? ' (cancelada)' : ''}.</span>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onMove('A_FAZER')}>Desarquivar</Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div role="group" aria-label="Etapa" className="flex flex-wrap gap-1">
        {BOARD_STAGES.map((s) => (
          <Button
            key={s.value}
            size="sm"
            type="button"
            variant={task.etapa === s.value ? 'default' : 'outline'}
            aria-pressed={task.etapa === s.value}
            disabled={busy || (task.etapa === s.value && s.value !== 'AGUARDANDO')}
            onClick={() => choose(s.value)}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {task.etapa === 'AGUARDANDO' && !askingWaiting && (
        <p className="text-sm text-muted-foreground">
          Aguardando <strong className="text-foreground">{task.waiting_on}</strong>: {task.waiting_for} · retorno em {formatDay(task.waiting_until)}
        </p>
      )}

      {askingWaiting && (
        <div className={cn('grid grid-cols-2 gap-2 rounded-md border p-3')}>
          <div className="col-span-2 flex flex-col gap-1">
            <Label htmlFor="waiting-on">Aguardando de quem</Label>
            <Input id="waiting-on" value={waiting.waiting_on} onChange={(e) => setWaiting({ ...waiting, waiting_on: e.target.value })} />
          </div>
          <div className="col-span-2 flex flex-col gap-1">
            <Label htmlFor="waiting-for">O quê</Label>
            <Input id="waiting-for" value={waiting.waiting_for} onChange={(e) => setWaiting({ ...waiting, waiting_for: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="waiting-until">Data de retorno</Label>
            <Input id="waiting-until" type="date" value={waiting.waiting_until} onChange={(e) => setWaiting({ ...waiting, waiting_until: e.target.value })} />
          </div>
          <div className="flex items-end justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => setAskingWaiting(false)}>Cancelar</Button>
            <Button
              type="button"
              size="sm"
              disabled={busy || !waitingValid}
              onClick={() => {
                onMove('AGUARDANDO', waiting)
                setAskingWaiting(false)
              }}
            >
              Aguardar
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {task.etapa === 'CONCLUIDA' && (
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onReopen}>
            <RotateCcw className="size-4" /> Reabrir
          </Button>
        )}
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onMove('ARQUIVADA')}>
          <Archive className="size-4" /> Arquivar
        </Button>
      </div>
    </div>
  )
}
